import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";

/* ── Brand palette (su sfondo scuro) ──────────────────────────────────── */
const TERRA = "#C4673A";
const OLIVE = "#5C6B3A";
const INK = "#1A1A1A";
const BG = "#141210";
const PANEL = "rgba(30,26,22,0.85)";
const TEXT = "#E8E2D8";
const TEXT_SOFT = "#A89F90";
const TEXT_FAINT = "#6B6357";

/* ── Animated counter ─────────────────────────────────────────────────── */
function useCounter(target, duration, startWhen) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!startWhen) return;
    let start = null;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      setValue(Math.floor(p * target));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, startWhen]);
  return value;
}

/* ── Intersection observer ────────────────────────────────────────────── */
function useVisible(threshold) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold: threshold || 0.1 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

/* ── Pipeline step ────────────────────────────────────────────────────── */
function PipelineStep({ step, label, detail, icon, delay, visible }) {
  return (
    <div
      className="flex flex-col items-center text-center px-2"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(30px)",
        transition: `opacity 0.6s ease ${delay}ms, transform 0.6s ease ${delay}ms`,
      }}
    >
      <div className="relative mb-3">
        <div
          className="w-16 h-16 rounded-2xl flex items-center justify-center text-2xl"
          style={{
            border: `1px solid ${TERRA}66`,
            background: `${TERRA}14`,
            boxShadow: `0 0 20px ${TERRA}33`,
          }}
        >
          {icon}
        </div>
        <div
          className="absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold"
          style={{ background: TERRA, color: "#fff" }}
        >
          {step}
        </div>
      </div>
      <div
        className="text-xs font-semibold mb-1 tracking-widest uppercase"
        style={{ color: TERRA, fontFamily: "monospace" }}
      >
        {label}
      </div>
      <div className="text-xs leading-relaxed" style={{ color: TEXT_SOFT, maxWidth: 120 }}>
        {detail}
      </div>
    </div>
  );
}

/* ── Area card ────────────────────────────────────────────────────────── */
function AreaCard({ icon, name, role, detail, bullets, badge, color, visible, delay }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        borderRadius: 18,
        border: `1px solid ${color}40`,
        background: PANEL,
        backdropFilter: "blur(12px)",
        padding: 28,
        position: "relative",
        overflow: "hidden",
        opacity: visible ? 1 : 0,
        transform: visible
          ? hovered ? "translateY(-6px)" : "translateY(0)"
          : "translateY(40px)",
        transition: `opacity 0.6s ease ${delay}ms, transform 0.4s ease, box-shadow 0.3s ease`,
        boxShadow: hovered ? `0 12px 40px ${color}33` : `0 0 20px ${color}12`,
      }}
    >
      {/* hover glow overlay */}
      <div
        style={{
          position: "absolute", inset: 0, pointerEvents: "none",
          background: `radial-gradient(circle at 50% 0%, ${color}1f, transparent 70%)`,
          opacity: hovered ? 1 : 0, transition: "opacity 0.4s ease",
        }}
      />
      <div style={{ position: "relative", zIndex: 1 }}>
        <div className="flex items-start justify-between mb-5">
          <div
            style={{
              width: 56, height: 56, borderRadius: 14,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 30, background: `${color}1a`,
              border: `1px solid ${color}40`,
            }}
          >
            {icon}
          </div>
          <span
            className="text-xs font-semibold px-3 py-1 rounded-full"
            style={{
              fontFamily: "monospace",
              color, border: `1px solid ${color}50`,
              background: `${color}15`,
            }}
          >
            {badge}
          </span>
        </div>
        <div
          className="text-xl font-bold mb-1"
          style={{ fontFamily: "'Cabinet Grotesk', sans-serif", color: TEXT }}
        >
          {name}
        </div>
        <div className="text-sm font-semibold mb-4" style={{ color }}>
          {role}
        </div>
        <div className="text-sm leading-relaxed mb-5" style={{ color: TEXT_SOFT }}>
          {detail}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {bullets.map((b, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span
                style={{
                  width: 6, height: 6, borderRadius: "50%",
                  background: color, flexShrink: 0,
                }}
              />
              <span style={{ fontSize: 13, color: TEXT_FAINT }}>{b}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Stat card ────────────────────────────────────────────────────────── */
function StatCard({ suffix, label, color, target, visible }) {
  const count = useCounter(target, 2000, visible);
  return (
    <div
      style={{
        textAlign: "center", padding: "28px 16px",
        borderRadius: 16, border: `1px solid ${color}33`,
        background: "rgba(30,26,22,0.5)",
      }}
    >
      <div
        style={{
          fontFamily: "monospace", fontSize: 48, fontWeight: 900,
          color, lineHeight: 1, marginBottom: 8,
        }}
      >
        {count}{suffix}
      </div>
      <div style={{ color: TEXT_SOFT, fontSize: 14 }}>{label}</div>
    </div>
  );
}

/* ── Main component ───────────────────────────────────────────────────── */
export default function TeamAI() {
  const [pipeRef, pipeVisible] = useVisible(0.05);
  const [areaRef, areaVisible] = useVisible(0.05);
  const [statsRef, statsVisible] = useVisible(0.15);
  const [philoRef, philoVisible] = useVisible(0.1);

  const pipeline = [
    { icon: "🔗", label: "URL", detail: "Inserisci il sito della tua azienda", step: "01" },
    { icon: "🧠", label: "Comprensione", detail: "Il sistema capisce chi sei in secondi", step: "02" },
    { icon: "⚙️", label: "Agente pronto", detail: "Voce su misura per la tua attività", step: "03" },
    { icon: "📞", label: "Chiamata", detail: "Parte la telefonata, naturale e fluida", step: "04" },
    { icon: "📱", label: "Notifica", detail: "Ricevi l'esito su Telegram", step: "05" },
  ];

  const areas = [
    {
      icon: "🧠",
      name: "Motore di Comprensione",
      role: "Capisce la tua azienda",
      detail:
        "Gli basta l'indirizzo del tuo sito. In pochi secondi legge, interpreta e costruisce l'identità della tua attività: cosa offri, come parli, cosa conta per i tuoi clienti.",
      bullets: [
        "Analisi del sito in tempo reale",
        "Tono di voce su misura",
        "Zero configurazione manuale",
      ],
      badge: "AREA 01",
      color: OLIVE,
    },
    {
      icon: "🎙️",
      name: "Agente Vocale",
      role: "Parla, ascolta, risponde",
      detail:
        "Una voce italiana naturale che sostiene una conversazione reale. Ascolta, capisce le risposte, gestisce le pause e le interruzioni come farebbe una persona al telefono.",
      bullets: [
        "Conversazione in tempo reale",
        "Voce indistinguibile dall'umano",
        "Reazione immediata, niente attese",
      ],
      badge: "AREA 02",
      color: TERRA,
    },
    {
      icon: "🛡️",
      name: "Sistema di Controllo",
      role: "Supervisiona h24",
      detail:
        "Sopra tutto vigila un sistema che non dorme mai. Controlla ogni chiamata, registra ogni esito e ti avvisa su Telegram nell'istante in cui qualcosa succede.",
      bullets: [
        "Monitoraggio attivo 24 ore su 24",
        "Notifiche istantanee su Telegram",
        "Storico e controllo sempre con te",
      ],
      badge: "AREA 03",
      color: "#C9A227",
    },
  ];

  return (
    <div style={{ background: BG, minHeight: "100vh", color: TEXT }}>

      {/* ── NAV DARK ───────────────────────────────────────────── */}
      <nav
        style={{
          position: "sticky", top: 0, zIndex: 50,
          background: "rgba(20,18,16,0.85)",
          backdropFilter: "blur(16px)",
          borderBottom: `1px solid ${TERRA}26`,
          padding: "0 24px",
        }}
      >
        <div
          className="max-w-6xl mx-auto flex items-center justify-between"
          style={{ height: 64 }}
        >
          <Link
            to="/"
            style={{
              color: TEXT_SOFT, textDecoration: "none", fontSize: 14,
              display: "flex", alignItems: "center", gap: 6,
              transition: "color 0.2s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = TERRA)}
            onMouseLeave={(e) => (e.currentTarget.style.color = TEXT_SOFT)}
          >
            ← Torna alla home
          </Link>
          <div
            style={{
              fontFamily: "'Cabinet Grotesk', sans-serif",
              fontWeight: 900, fontSize: 22, color: TEXT,
              letterSpacing: "-0.02em",
            }}
          >
            24<span style={{ color: TERRA }}>Vox</span>
          </div>
          <div
            style={{
              display: "flex", alignItems: "center", gap: 6,
              fontSize: 12, fontFamily: "monospace", color: OLIVE,
            }}
          >
            <span
              style={{
                width: 8, height: 8, borderRadius: "50%",
                background: "#7C9447",
                animation: "pulse 2s infinite",
              }}
            />
            <span style={{ color: "#9CB466" }}>LIVE</span>
          </div>
        </div>
      </nav>

      {/* ── HERO ───────────────────────────────────────────────── */}
      <section
        style={{ position: "relative", overflow: "hidden", padding: "96px 24px 80px" }}
      >
        {/* Grid bg */}
        <div
          style={{
            position: "absolute", inset: 0, pointerEvents: "none",
            backgroundImage:
              `linear-gradient(${TERRA}0a 1px, transparent 1px), linear-gradient(90deg, ${TERRA}0a 1px, transparent 1px)`,
            backgroundSize: "40px 40px",
          }}
        />
        {/* Radial glow */}
        <div
          style={{
            position: "absolute", top: "40%", left: "50%",
            transform: "translate(-50%,-50%)",
            width: 700, height: 460, borderRadius: "50%", pointerEvents: "none",
            background: `radial-gradient(ellipse, ${TERRA}1f, transparent 70%)`,
          }}
        />
        <div
          className="relative max-w-4xl mx-auto text-center"
          style={{ zIndex: 1 }}
        >
          <div
            style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              padding: "8px 20px", borderRadius: 999,
              border: `1px solid ${TERRA}4d`,
              background: `${TERRA}14`,
              color: TERRA, fontSize: 13, fontFamily: "monospace",
              marginBottom: 32,
            }}
          >
            <span
              style={{
                width: 8, height: 8, borderRadius: "50%",
                background: TERRA, animation: "pulse 2s infinite",
              }}
            />
            1 UMANO + AI = EFFICIENZA MASSIMA
          </div>

          <h1
            style={{
              fontFamily: "'Cabinet Grotesk', sans-serif",
              fontSize: "clamp(38px, 6.5vw, 76px)",
              fontWeight: 900, lineHeight: 1.05,
              marginBottom: 24, color: TEXT,
            }}
          >
            Perché costa così poco
            <br />
            <span
              style={{
                background: `linear-gradient(135deg, ${TERRA} 0%, #E0A05A 100%)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              e funziona così bene
            </span>
          </h1>

          <p
            style={{
              color: TEXT_SOFT, fontSize: 18, lineHeight: 1.7,
              maxWidth: 600, margin: "0 auto",
            }}
          >
            Niente reparti, niente call center, niente costi nascosti. Solo{" "}
            <strong style={{ color: TEXT }}>una persona</strong> affiancata da una{" "}
            <strong style={{ color: TERRA }}>tecnologia proprietaria</strong> che fa il
            lavoro di un intero ufficio. Ecco le tre aree che la fanno funzionare.
          </p>
        </div>
      </section>

      {/* ── PIPELINE ───────────────────────────────────────────── */}
      <section style={{ padding: "48px 24px 64px" }} ref={pipeRef}>
        <div className="max-w-5xl mx-auto">
          <div style={{ textAlign: "center", marginBottom: 56 }}>
            <div
              style={{
                color: TERRA, fontFamily: "monospace",
                fontSize: 12, letterSpacing: "0.15em",
                textTransform: "uppercase", marginBottom: 12,
              }}
            >
              // DALL'URL ALLA CHIAMATA
            </div>
            <h2
              style={{
                fontFamily: "'Cabinet Grotesk', sans-serif",
                fontSize: 36, fontWeight: 800, color: TEXT, marginBottom: 8,
              }}
            >
              Tu inserisci l'indirizzo. Al resto pensa lei.
            </h2>
            <p style={{ color: TEXT_FAINT, fontSize: 15 }}>
              Cinque passaggi automatici. Nessun tecnico. Pochi secondi in tutto.
            </p>
          </div>

          {/* Steps grid */}
          <div style={{ position: "relative" }}>
            {/* Connector line (desktop) */}
            <div
              style={{
                position: "absolute", top: 32, left: "10%", right: "10%",
                height: 1, pointerEvents: "none",
                background:
                  `linear-gradient(90deg, transparent, ${TERRA}, ${OLIVE}, ${TERRA}, transparent)`,
              }}
              className="hidden md:block"
            />
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: 32,
              }}
              className="md:grid-cols-5"
            >
              {pipeline.map((s, i) => (
                <PipelineStep
                  key={i}
                  step={s.step}
                  label={s.label}
                  detail={s.detail}
                  icon={s.icon}
                  delay={i * 100}
                  visible={pipeVisible}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── LE TRE AREE ────────────────────────────────────────── */}
      <section style={{ padding: "64px 24px" }} ref={areaRef}>
        <div className="max-w-6xl mx-auto">
          <div style={{ textAlign: "center", marginBottom: 56 }}>
            <div
              style={{
                color: OLIVE, fontFamily: "monospace",
                fontSize: 12, letterSpacing: "0.15em",
                textTransform: "uppercase", marginBottom: 12,
              }}
            >
              // LE TRE AREE
            </div>
            <h2
              style={{
                fontFamily: "'Cabinet Grotesk', sans-serif",
                fontSize: 36, fontWeight: 800, color: TEXT, marginBottom: 8,
              }}
            >
              Tre cervelli che lavorano insieme
            </h2>
            <p style={{ color: TEXT_FAINT, fontSize: 15, maxWidth: 540, margin: "0 auto" }}>
              Ognuno fa una cosa sola, e la fa meglio di chiunque altro. Insieme
              sostituiscono un intero reparto.
            </p>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
              gap: 24,
            }}
          >
            {areas.map((a, i) => (
              <AreaCard key={i} {...a} visible={areaVisible} delay={i * 120} />
            ))}
          </div>
        </div>
      </section>

      {/* ── STATS ──────────────────────────────────────────────── */}
      <section style={{ padding: "64px 24px" }} ref={statsRef}>
        <div className="max-w-4xl mx-auto">
          <div style={{ textAlign: "center", marginBottom: 48 }}>
            <div
              style={{
                color: TERRA, fontFamily: "monospace",
                fontSize: 12, letterSpacing: "0.15em",
                textTransform: "uppercase", marginBottom: 12,
              }}
            >
              // I NUMERI
            </div>
            <h2
              style={{
                fontFamily: "'Cabinet Grotesk', sans-serif",
                fontSize: 36, fontWeight: 800, color: TEXT,
              }}
            >
              Quello che conta davvero.
            </h2>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: 16,
            }}
            className="md:grid-cols-4"
          >
            <StatCard suffix="h"  label="Operativa ogni giorno"    color={TERRA}     target={24}  visible={statsVisible} />
            <StatCard suffix="€"  label="All'anno, tutto incluso"  color={OLIVE}     target={588} visible={statsVisible} />
            <StatCard suffix=""   label="Persona, infiniti clienti" color="#C9A227"  target={1}   visible={statsVisible} />
            <StatCard suffix="min" label="E sei già operativa"      color="#E0A05A"  target={5}   visible={statsVisible} />
          </div>
        </div>
      </section>

      {/* ── LA FILOSOFIA ───────────────────────────────────────── */}
      <section style={{ padding: "64px 24px" }} ref={philoRef}>
        <div className="max-w-4xl mx-auto">
          <div
            style={{
              borderRadius: 24,
              border: `1px solid ${TERRA}33`,
              background: PANEL,
              padding: "56px 40px",
              position: "relative",
              overflow: "hidden",
              opacity: philoVisible ? 1 : 0,
              transform: philoVisible ? "translateY(0)" : "translateY(40px)",
              transition: "opacity 0.8s ease, transform 0.8s ease",
            }}
          >
            <div
              style={{
                position: "absolute", inset: 0, pointerEvents: "none",
                background: `radial-gradient(circle at 80% 20%, ${OLIVE}1f, transparent 60%)`,
              }}
            />
            <div style={{ position: "relative", zIndex: 1, textAlign: "center" }}>
              <div
                style={{
                  color: OLIVE, fontFamily: "monospace",
                  fontSize: 12, letterSpacing: "0.15em",
                  textTransform: "uppercase", marginBottom: 20,
                }}
              >
                // LA FILOSOFIA
              </div>
              <h2
                style={{
                  fontFamily: "'Cabinet Grotesk', sans-serif",
                  fontSize: "clamp(26px, 4vw, 40px)",
                  fontWeight: 800, color: TEXT,
                  lineHeight: 1.2, marginBottom: 24, maxWidth: 640,
                  margin: "0 auto 24px",
                }}
              >
                Una scatola nera che{" "}
                <span style={{ color: TERRA }}>funziona</span>.
              </h2>
              <p
                style={{
                  color: TEXT_SOFT, fontSize: 18, lineHeight: 1.7,
                  maxWidth: 600, margin: "0 auto 32px",
                }}
              >
                Non devi capire come è fatta dentro. Non devi installare niente, non devi
                chiamare un tecnico, non devi leggere un manuale.{" "}
                <strong style={{ color: TEXT }}>Tu inserisci l'URL, e ricevi la chiamata.</strong>{" "}
                Tutto il resto — la tecnologia proprietaria, il controllo, la voce — vive
                dentro la scatola. A te resta solo il risultato.
              </p>

              {/* black box flow */}
              <div
                style={{
                  display: "flex", alignItems: "center", justifyContent: "center",
                  gap: 20, flexWrap: "wrap",
                }}
              >
                <div
                  style={{
                    fontFamily: "monospace", fontSize: 14,
                    color: TEXT_SOFT, padding: "10px 18px",
                    borderRadius: 10, border: `1px solid ${TEXT_FAINT}55`,
                    background: "rgba(0,0,0,0.25)",
                  }}
                >
                  🔗 il tuo URL
                </div>
                <span style={{ color: TERRA, fontSize: 24 }}>→</span>
                <div
                  style={{
                    fontFamily: "monospace", fontSize: 14, fontWeight: 700,
                    color: "#fff", padding: "10px 18px",
                    borderRadius: 10, border: `1px solid ${TERRA}`,
                    background: INK,
                    boxShadow: `0 0 24px ${TERRA}40`,
                  }}
                >
                  ⬛ 24Vox
                </div>
                <span style={{ color: TERRA, fontSize: 24 }}>→</span>
                <div
                  style={{
                    fontFamily: "monospace", fontSize: 14,
                    color: TEXT_SOFT, padding: "10px 18px",
                    borderRadius: 10, border: `1px solid ${OLIVE}99`,
                    background: "rgba(0,0,0,0.25)",
                  }}
                >
                  📞 la chiamata
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ────────────────────────────────────────────────── */}
      <section style={{ padding: "80px 24px", textAlign: "center" }}>
        <div style={{ maxWidth: 560, margin: "0 auto" }}>
          <h2
            style={{
              fontFamily: "'Cabinet Grotesk', sans-serif",
              fontSize: "clamp(28px, 5vw, 44px)",
              fontWeight: 800, color: TEXT,
              lineHeight: 1.15, marginBottom: 16,
            }}
          >
            Tutta questa potenza.
            <br />
            <span
              style={{
                background: `linear-gradient(135deg, ${TERRA}, ${OLIVE})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Per la tua azienda.
            </span>
          </h2>
          <p style={{ color: TEXT_FAINT, fontSize: 16, marginBottom: 36 }}>
            588€/anno. Nessun tecnico, nessuna configurazione. Parte in 5 minuti.
          </p>
          <Link
            to="/attivazione"
            style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              padding: "16px 36px", borderRadius: 999,
              background: `linear-gradient(135deg, ${TERRA}, ${OLIVE})`,
              color: "#fff", fontWeight: 700, fontSize: 16,
              textDecoration: "none",
              transition: "transform 0.2s ease, box-shadow 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "scale(1.04)";
              e.currentTarget.style.boxShadow = `0 0 30px ${TERRA}66`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "scale(1)";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            Attiva la tua 24Vox →
          </Link>
        </div>
      </section>

      {/* ── FOOTER DARK ────────────────────────────────────────── */}
      <footer
        style={{
          borderTop: `1px solid ${TEXT_FAINT}33`,
          padding: "32px 24px",
          textAlign: "center",
        }}
      >
        <div
          style={{
            fontFamily: "'Cabinet Grotesk', sans-serif",
            fontWeight: 900, fontSize: 20, color: TEXT_FAINT,
            marginBottom: 12,
          }}
        >
          24<span style={{ color: TERRA }}>Vox</span>
        </div>
        <div style={{ display: "flex", justifyContent: "center", gap: 24, fontSize: 13 }}>
          <Link to="/privacy" style={{ color: TEXT_FAINT, textDecoration: "none" }}>Privacy</Link>
          <Link to="/termini" style={{ color: TEXT_FAINT, textDecoration: "none" }}>Termini</Link>
          <Link to="/" style={{ color: TEXT_FAINT, textDecoration: "none" }}>Home</Link>
        </div>
        <div style={{ color: TEXT_FAINT, fontSize: 12, marginTop: 16 }}>
          © 2025 24Vox — 1 umano + AI = efficienza massima
        </div>
      </footer>

    </div>
  );
}
